import { useState, useEffect, useRef, useCallback } from 'react';
import { useHasRolePermissionToComponentMutation } from '../components/services/services';

interface HasRolePermissionToComponent {
  code: string;
}

interface UsePermissionOptions {
  code: string;
  enabled?: boolean; // Hook'un çalışıp çalışmayacağını kontrol eder
}

interface UsePermissionReturn {
  hasPermission: boolean;
  isLoading: boolean;
  error: any;
  refetch: () => void;
}

// Global cache: Aynı permission code için devam eden istekleri ve sonuçları saklar
interface PermissionCacheItem {
  promise: Promise<any>;
  result?: { hasPermission: boolean; error?: any };
  subscribers: Set<() => void>;
}

const permissionCache = new Map<string, PermissionCacheItem>();

/**
 * Tüm permission cache'ini temizler.
 * Özellikle logout / login sonrasında eski kullanıcının
 * yetkilerinin yeni kullanıcıya taşınmaması için kullanılmalıdır.
 */
export const clearPermissionCache = () => {
  permissionCache.clear();
};

/**
 * Kullanıcının belirli bir işlem için yetkisi olup olmadığını kontrol eden hook
 * @param options - Permission kodunu ve diğer seçenekleri içeren obje
 * @returns Permission durumu, loading state ve hata bilgisi
 */
export const usePermission = (options: UsePermissionOptions): UsePermissionReturn => {
  const { code, enabled = true } = options;
  const [hasPermission, setHasPermission] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true); // Başlangıçta loading true olmalı
  const [error, setError] = useState<any>(null);
  const isMounted = useRef<boolean>(true);
  const updateStateRef = useRef<(() => void) | undefined>(undefined);

  const [hasRolePermissionToComponent] = useHasRolePermissionToComponentMutation();

  // State güncelleme fonksiyonu - ref kullanarak her zaman aynı referansı koru
  const updateState = useCallback(() => {
    if (!isMounted.current) return;
    
    const cacheItem = permissionCache.get(code);
    if (cacheItem?.result) {
      setHasPermission(cacheItem.result.hasPermission);
      setError(cacheItem.result.error);
      setIsLoading(false);
    }
  }, [code]);

  // State güncelleme referansını sakla
  updateStateRef.current = updateState;

  const checkPermission = useCallback(async () => {
    if (!enabled || !code) {
      if (isMounted.current) {
        setIsLoading(false);
      }
      return;
    }

    // Cache'de bu code için bir item var mı kontrol et
    let cacheItem = permissionCache.get(code);

    if (cacheItem) {
      // Eğer sonuç varsa direkt kullan
      if (cacheItem.result) {
        if (isMounted.current) {
          setHasPermission(cacheItem.result.hasPermission);
          setError(cacheItem.result.error);
          setIsLoading(false);
        }
        // Subscriber listesine ekle (gelecekte refetch için)
        if (updateStateRef.current) {
          cacheItem.subscribers.add(updateStateRef.current);
        }
        return;
      }

      // Eğer istek devam ediyorsa, subscriber olarak ekle ve bekle
      if (isMounted.current) {
        setIsLoading(true);
      }
      if (updateStateRef.current) {
        cacheItem.subscribers.add(updateStateRef.current);
      }
      
      try {
        await cacheItem.promise;
        // Promise tamamlandığında state güncelle
        updateState();
      } catch (err) {
        if (isMounted.current) {
          setError(err);
          setIsLoading(false);
        }
      }
      return;
    }

    // Yeni istek başlat
    if (isMounted.current) {
      setIsLoading(true);
      setError(null);
    }

    // Cache item oluştur
    const permissionRequest: HasRolePermissionToComponent = { code };
    const promise = hasRolePermissionToComponent(permissionRequest).unwrap();

    cacheItem = {
      promise,
      subscribers: new Set(updateStateRef.current ? [updateStateRef.current] : [])
    };
    permissionCache.set(code, cacheItem);

    try {
      const response = await promise;
      const result = {
        hasPermission: !!response?.data,
        error: undefined
      };

      // Sonucu cache'e kaydet
      cacheItem.result = result;

      // Tüm subscriber'ları bilgilendir
      cacheItem.subscribers.forEach(subscriber => {
        try {
          subscriber();
        } catch (e) {
          // Subscriber hata verirse görmezden gel
        }
      });

      // Component hala mount durumundaysa state'i güncelle
      if (isMounted.current) {
        setHasPermission(result.hasPermission);
        setIsLoading(false);
      }
    } catch (err) {
      const result = {
        hasPermission: false,
        error: err
      };

      cacheItem.result = result;

      // Tüm subscriber'ları bilgilendir
      cacheItem.subscribers.forEach(subscriber => {
        try {
          subscriber();
        } catch (e) {
          // Subscriber hata verirse görmezden gel
        }
      });

      if (isMounted.current) {
        setError(err);
        setHasPermission(false);
        setIsLoading(false);
      }
    }
  }, [code, enabled, hasRolePermissionToComponent, updateState]);

  useEffect(() => {
    isMounted.current = true;
    checkPermission();
    
    // Cleanup function - component unmount olduğunda subscriber'dan çıkar
    return () => {
      isMounted.current = false;
      const cacheItem = permissionCache.get(code);
      if (cacheItem && updateStateRef.current) {
        cacheItem.subscribers.delete(updateStateRef.current);
        // Eğer subscriber kalmadıysa cache'i temizle (opsiyonel)
        // if (cacheItem.subscribers.size === 0) {
        //   permissionCache.delete(code);
        // }
      }
    };
  }, [checkPermission, code]); // checkPermission ve code değiştiğinde tekrar çalışır

  const refetch = useCallback(() => {
    // Cache'i temizle ve tekrar istek at
    permissionCache.delete(code);
    checkPermission();
  }, [code, checkPermission]);

  return {
    hasPermission,
    isLoading,
    error,
    refetch
  };
};

/**
 * Birden fazla permission kodunu kontrol eden hook
 * @param codes - Kontrol edilecek permission kodları array'i
 * @param enabled - Hook'un çalışıp çalışmayacağını kontrol eder
 * @returns Her permission kodu için durum bilgisi
 */
export const useMultiplePermissions = (
  codes: string[],
  enabled: boolean = true
): Record<string, UsePermissionReturn> => {
  const permissions: Record<string, UsePermissionReturn> = {};

  codes.forEach(code => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    permissions[code] = usePermission({ code, enabled });
  });

  return permissions;
};
